import {captureException, captureMessage} from '@sentry/react';
import {FORM_TYPE, FormDataType, LeadFormDataType} from 'api/types/LeadForm';
import {PhoneVerificationUserInfo, VerifyPhoneCodeToAttempt} from 'api/types/PhoneVerification';
import {useCallback, useRef, useMemo} from 'react';
import {apiUrl, headers, isDev} from 'utils/constants';
import useAnalytics from 'utils/hooks/useAnalytics';
import useMutation from 'utils/hooks/useMutation';
import {useSearchBaseParams} from 'utils/searchParamsContext';
import {FullUserInfo} from 'utils/userInfoContext';
// import leadFormMock from './lead_form_mock'
// import surveyMock from './survey_mock';
import {getLead} from 'api/getLead';
import {Batcher, BatcherState} from 'utils/batcher';
import {getLeadFormRequestUrl, searchParamsWithFormDataHash} from 'utils/utils';
import {formStartAnalyticEvent} from './utils';
import {initRequest, initRequestDataHash} from 'init';
import {LeadFormStateInner} from 'core/types';
import {CaptchaViewMode, CaptchaWidget, CaptchaColorScheme} from '@vkid/captcha';
import {CHECK_CAPTCHA_STATUS, checkCaptchaResult} from 'utils/types';
import {AppearanceType} from '@vkontakte/vk-bridge';
import {EventEnum} from 'api/types/Analytics';
import {BlsFormPagePage, BlsFormPageResultInfo, BlsResponse} from 'api/types/Bls';
import {PAGE_TYPE} from 'api/types/types';
import {BlsFormQuestion, BlsFormPageResultInfo as BlsFormPageResultInfoView} from '@target-web/leadads-form-view';

const mutationOptions = {
    method: 'POST',
    headers,
};

const devMutation: ReturnType<typeof useMutation> = [
    {},
    (_: BodyInit) => {
        //@ts-ignore
        let data = JSON.parse(_);
        const key = Object.keys(data)[0];
        data = data[key] || [data];
        //@ts-ignore
        let msg = data.map((item) => `Event: ${item?.event} ${item?.data ? JSON.stringify(item.data) : ''}`);
        if (msg.length > 1) msg = 'Chunk:\n\t' + msg.join('\n\t');
        else msg = msg[0];
        const allowedEvents: EventEnum[] = []; // [EventEnum.resource_loading_error]
        const isAllowed = allowedEvents.length > 0 ? allowedEvents.some((eventName) => msg.includes(eventName)) : false;
        if (isAllowed) {
            console.log(msg);
        }
    },
];

export const cacheLFData: Record<string, FormDataType> = {};

export const getLeadFormData = async (
    getLeadFormDataAnalyticEvent: ReturnType<typeof useAnalytics>['getLeadFormDataAnalyticEvent'],
    signal: AbortSignal | undefined,
    searchParams: searchParamsWithFormDataHash,
): Promise<FormDataType> => {
    const {formDataHash} = searchParams;
    const defaultPromise = formDataHash === initRequestDataHash && initRequest;

    if (formDataHash in cacheLFData) {
        return cacheLFData[formDataHash];
    }

    if (!defaultPromise) {
        performance.mark('getLeadFormDataStart');
    }

    const res = defaultPromise
        ? await defaultPromise
        : await fetch(getLeadFormRequestUrl(searchParams), {
              headers,
              signal,
          });

    formStartAnalyticEvent(!!defaultPromise, getLeadFormDataAnalyticEvent);

    // #TODO код ошибки нужно будет потом перенести на новую страницу с ошибкой
    if (!res.ok) {
        throw new Error(
            JSON.stringify({
                status: res.status,
                body: await res.text(),
            }),
        );
    }

    let data = await res.json();

    // для тестирования уже заполненной формы
    if (searchParams.forced_filling) {
        data.is_filled_previously = false;
    }

    if (isDev) {
        // const search = window.location.search;
        // const hash = window.location.hash.replace(/^(#\?|#&|#|\?|&)/g, '');
        // const params = new URLSearchParams(search ? search + '&' + hash : '?' + hash);
        // if (params.get('test')) {
        //     data = surveyMock;
        // }
        // место для подмены данных
        // const data = mock as LeadFormType
        // data.pages = []
        data.is_filled_previously = false;
        // data.ads_consent_url = 'https://ya.ru';
        // data.landing = true;
        // data.main_image = {
        //     id: 'fafsafsa',
        //     variants: {
        //         original: 'https://r.mradx.net/img/6D/70C50F.jpg',
        //         '320x180': 'https://r.mradx.net/img/6D/70C50F.jpg',
        //         '480x270': 'https://r.mradx.net/img/6D/70C50F.jpg',
        //         '1080x607': 'https://r.mradx.net/img/6D/70C50F4.jpg',
        //     },
        // };
        // delete data.main_image;
        // data.form.data.data.sequence_type = 'steps';
        // data.form.data.data.style.header_background_new = [];
    }

    // в дате мы не получаем тип, но
    // у лидформы есть только поле form_id
    // у опроса есть только поле id
    if (searchParams.formDataType === FORM_TYPE.business_card) {
        data.type = FORM_TYPE.business_card;
    } else if (searchParams.formDataType === FORM_TYPE.bls) {
        data.type = FORM_TYPE.bls;

        const blsData = data as BlsResponse;

        function isPage(page: BlsFormPagePage | BlsFormPageResultInfo): page is BlsFormPagePage {
            return page.type === PAGE_TYPE.page;
        }

        const questions = blsData.pages.filter(isPage).map(({page: {id, blocks}}) => ({
            page_id: id,
            block_id: blocks[0].id,
            ...blocks[0].block_data.data,
        })) satisfies BlsFormQuestion[];
        const result_info = blsData.pages.find(({type}) => type === PAGE_TYPE.result_info) as BlsFormPageResultInfo;

        data.questions = questions;
        data.result_info = {...result_info.page} satisfies BlsFormPageResultInfoView;
    } else if (data.form_id) {
        data.type = FORM_TYPE.leadform;
        data.agreement_only = searchParams.onlyAgreement;
    } else if (data.id) {
        data.type = FORM_TYPE.survey;
        // @ts-ignore // FOR test survey answer images
        // data.page.page.blocks[0].block_data.data.answers.slice(0, 1).forEach((answer) => {
        //     answer.image = {
        //         id: 'fasfasf',
        //         variants: {
        //             '72x72': 'https://a.d-cd.net/SMay7QnbFAUhw70qmi6XYj2HaCA-480.jpg',
        //             '256x256': 'https://a.d-cd.net/SMay7QnbFAUhw70qmi6XYj2HaCA-480.jpg',
        //             original: 'https://a.d-cd.net/SMay7QnbFAUhw70qmi6XYj2HaCA-480.jpg',
        //         },
        //     };
        // });
        // @ts-ignore // FOR test survey answer images
        // data.page.page.blocks[0].block_data.data.answers.slice(0, 1).forEach((answer) => {
        //     answer.image = {
        //         id: 'fasfasf',
        //         variants: {
        //             '72x72': 'https://a.d-cd.net/SMay7QnbFAUhw70qmi6XYj2HaCA-480.jpg',
        //             '256x256': 'https://a.d-cd.net/SMay7QnbFAUhw70qmi6XYj2HaCA-480.jpg',
        //             original: 'https://a.d-cd.net/SMay7QnbFAUhw70qmi6XYj2HaCA-480.jpg',
        //         },
        //     };
        // });
        // @ts-ignore
        // data.page.page.blocks[0].block_data.data.answers.forEach((answer) => {
        //     answer.hasPreviewIcon = true;
        // });
        // FOR test survey question image
        // data.page.page.blocks[0].block_data.data.image = {
        //     id: 'fasfasf',
        //     variants: {
        //         '320x180': 'https://s.mradx.net/1080x607/q60/la/f/8f/004f35048b.jpeg',
        //         '480x270': 'https://s.mradx.net/1080x607/q60/la/f/8f/004f35048b.jpeg',
        //         '1080x607': 'https://s.mradx.net/1080x607/q60/la/f/8f/004f35048b.jpeg',
        //         original: 'https://s.mradx.net/1080x607/q60/la/f/8f/004f35048b.jpeg',
        //     },
        // };
    } else {
        const {form, company, ...props} = data;

        // если картинки нет - приходит пустой массив
        if (Array.isArray(form.data.data.style.header_background_new)) {
            delete form.data.data.style.header_background_new;
        }

        data = {
            data: {
                company: company.data.data,
                form: form.data.data,
                ...props,
            },
            type: FORM_TYPE.yclients,
        };

        if (isDev) {
            // для теста экспа быстрой записи
            // data.data.is_quick_write_enable = true;
        }
    }

    cacheLFData[formDataHash] = data;

    return data;
};

export const useLeadFormData = (setData: (data: FormDataType | null) => void) => {
    const controllerRef = useRef<AbortController | null>();
    const {getLeadFormDataAnalyticEvent} = useAnalytics();

    const updateLeadFormData = useCallback(
        async (params: searchParamsWithFormDataHash) => {
            if (controllerRef.current) {
                controllerRef.current.abort();
            }
            try {
                controllerRef.current = new AbortController();
            } catch (e) {
                captureException(e);
            }

            try {
                const leadFormData = await getLeadFormData(
                    getLeadFormDataAnalyticEvent,
                    controllerRef.current?.signal,
                    params,
                );

                setData(leadFormData);
                controllerRef.current = null;
            } catch (e) {
                captureException(e);
            }
        },
        [setData, getLeadFormDataAnalyticEvent],
    );

    return updateLeadFormData;
};

export const sendLead = (leadFormDataHash: string, body: BodyInit) => {
    return fetch(`${apiUrl}/leads?${new URLSearchParams({lead_form_data: leadFormDataHash})}`, {
        ...mutationOptions,
        body,
    });
};

export const sendBlsLead = (leadFormDataHash: string, body: BodyInit) => {
    return fetch(`${apiUrl}/bls?${new URLSearchParams({lead_form_data: leadFormDataHash})}`, {
        ...mutationOptions,
        body,
    });
};

export const sendSurveyAnswer = (leadFormDataHash: string, sessionId: string, body: BodyInit) => {
    return fetch(
        `${apiUrl}/survey/pages?${new URLSearchParams({
            lead_form_data: leadFormDataHash,
            session_id: sessionId,
        })}`,
        {
            ...mutationOptions,
            body,
        },
    );
};

export const useBusinessCardAnalyticsMutation = (): ReturnType<typeof useMutation> => {
    const mutation = useMutation(`${apiUrl}/business_card`, mutationOptions);
    // #TODO сделать так чтоб без formDataHash не отрабатывало ничего
    const [state, mutationState] = process.env.NODE_ENV === 'production' ? mutation : devMutation;

    const mutationStateWithQuery = useCallback<typeof mutationState>(
        (body: BodyInit, query?: URLSearchParams) => {
            return mutationState(body, query);
        },
        [mutationState],
    );

    const finalMutation: ReturnType<typeof useMutation> = [state, mutationStateWithQuery];
    return finalMutation;
};

export const useLeadFormAnalyticsMutation = (): ReturnType<typeof useMutation> => {
    const {formDataHash, isLeadFormType, isBlsType, platform, vk_ref, vk_platform, vk_app_id} = useSearchBaseParams();

    const dataType = isLeadFormType ? 'leads' : isBlsType ? 'bls' : 'survey';
    const eventChunkName = isLeadFormType ? 'lead_form_events' : isBlsType ? 'survey_events' : 'survey_events';

    const mutation = useMutation(
        `${apiUrl}/${dataType}/save_events?${new URLSearchParams({
            lead_form_data: formDataHash || '',
            platform,
            ...(vk_ref ? {vk_ref} : {}),
            ...(vk_platform ? {vk_platform} : {}),
            ...(vk_app_id ? {vk_app_id} : {}),
        })}`,
        mutationOptions,
    );
    // #TODO сделать так чтоб без formDataHash не отрабатывало ничего
    const [state, mutationState] = process.env.NODE_ENV === 'production' && formDataHash ? mutation : devMutation;
    const storage = useMemo(
        () =>
            new Batcher((...args: BodyInit[]) => {
                mutationState(`{"${eventChunkName}": [${args.join(', ')}]}`);
            }),
        [mutationState, eventChunkName],
    );

    const mutationStateWithUseStorage = useCallback<typeof mutationState>(
        (body: BodyInit) => {
            if (storage.state === BatcherState.inactive) return mutationState(`{"${eventChunkName}": [${body}]}`);
            storage.add(body);
        },
        [mutationState, storage, eventChunkName],
    );

    const finalMutation: ReturnType<typeof useMutation> = [state, mutationStateWithUseStorage];
    return finalMutation;
};

// этот постмессадж для фрейма веб дзена https://jira.vk.team/browse/TRG-177618
const sendPostMessageAfterLeadSent = (formData: LeadFormDataType, status: 'success' | 'failed') => {
    window.parent &&
        window.self !== window.top &&
        window.parent.postMessage(
            {
                event: 'leadSent',
                status,
                resultInfo: {
                    hasPhone: !!formData.result_info?.phone,
                    hasPromo: !!formData.result_info?.promo_code,
                    hasSiteUrl: !!formData.result_info?.site_url,
                },
            },
            '*',
        );
};

export const checkCaptcha = async (captchaUrl: string, appearance: AppearanceType): Promise<checkCaptchaResult> => {
    // схема тут https://confluence.vk.team/pages/viewpage.action?pageId=1364043560
    const captchaWidget = new CaptchaWidget();

    // метод show() возвращает промис с токеном при успешном прохождении капчи
    return await captchaWidget
        .show({
            container: document.body, // HTML элемент, в котором будет отображаться виджет капчи
            iframeSrc: captchaUrl,
            view: CaptchaViewMode.POPUP, // вид капчи: 'popup' или 'block'
            scheme: appearance === 'light' ? CaptchaColorScheme.LIGHT : CaptchaColorScheme.DARK, // Цветовая схема виджета (опционально)
        })
        .then((token) => {
            return {
                status: CHECK_CAPTCHA_STATUS.success,
                token,
            };
        })
        .catch((error) => {
            // пока никак не обрабатываем простое закрытие капчи
            if (error !== 'close') {
                captureException(error);
                captureMessage('Капча выбросила неожиданную ошибку', 'error');
            }
            return {
                status: error === 'close' ? CHECK_CAPTCHA_STATUS.cancel : CHECK_CAPTCHA_STATUS.error,
            };
        });
};

export const processError = async (response: Response) => {
    let error = `Failed send lead: ${response.status}`;
    try {
        const parsedRes = await response.json();
        if (parsedRes.error && (parsedRes.error.code || parsedRes.error.message)) {
            if (parsedRes.error.code) {
                error += `; code: ${parsedRes.error.code}`;
            }
            if (parsedRes.error.message) {
                error += `; message: ${parsedRes.error.message}`;
            }
        } else {
            error += '; unkown error format';
        }
    } catch (e) {
        error += '; failed parse response.json';
    }
    return error;
};

export const trySendLead = ({
    formData,
    formState,
    formDataHash,
    consentStatus,
    userInfo,
    canSendSocialMediaProfile,
    captchaData,
}: {
    formData: LeadFormDataType;
    formState: LeadFormStateInner;
    formDataHash: string;
    consentStatus: boolean;
    userInfo: FullUserInfo;
    canSendSocialMediaProfile: boolean;
    captchaData?: {
        session_token: string;
        captcha_token: string;
    };
}) => {
    let requestParams = getLead({
        formState,
        leadForm: formData,
        userInfo,
        consentStatus,
        canSendSocialMediaProfile,
    });

    if (captchaData) {
        requestParams = {
            ...requestParams,
            ...captchaData,
        };
    }

    return sendLead(formDataHash, JSON.stringify(requestParams))
        .then(async (response) => {
            if (response.ok) {
                sendPostMessageAfterLeadSent(formData, 'success');
                formData.is_filled_previously = true;
                return;
            }

            const error = await processError(response);
            throw new Error(error);
        })
        .catch((e) => {
            sendPostMessageAfterLeadSent(formData, 'failed');
            captureException(e);
            // возможно позже пригодится
            // captureException(e, {
            //     extra: {
            //         formDataHash,
            //         ...requestParams,
            //     },
            // });
            throw new Error(e);
        });
};

//  PHONE VERIFICATION
export const checkPhoneVerificationRequest = (leadFormDataHash: string, data: PhoneVerificationUserInfo) => {
    const body = JSON.stringify(data);
    const url = apiUrl;
    return fetch(`${url}/verify?${new URLSearchParams({lead_form_data: leadFormDataHash})}`, {
        ...mutationOptions,
        body,
    });
};

export const sendVerifyCodeRequest = (leadFormDataHash: string, data: PhoneVerificationUserInfo) => {
    const body = JSON.stringify(data);
    const url = apiUrl;
    return fetch(`${url}/verify/send?${new URLSearchParams({lead_form_data: leadFormDataHash})}`, {
        ...mutationOptions,
        body,
    });
};

export const attemptVerifyCodeRequest = (leadFormDataHash: string, data: VerifyPhoneCodeToAttempt) => {
    const body = JSON.stringify(data);
    const url = apiUrl;
    return fetch(`${url}/verify/attempt?${new URLSearchParams({lead_form_data: leadFormDataHash})}`, {
        ...mutationOptions,
        body,
    });
};

export const sendYClientsVerifyCodeRequest = (leadFormDataHash: string, data: PhoneVerificationUserInfo) => {
    const body = JSON.stringify(data);
    const url = apiUrl;
    return fetch(`${url}/yclients/book_code?${new URLSearchParams({lead_form_data: leadFormDataHash})}`, {
        ...mutationOptions,
        body,
    });
};
